import { Injectable } from '@nestjs/common';
import { Category } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { CategorySummary, CreateCategoryRequest, UpdateCategoryRequest } from '@cart/shared-types';
import { StoreContextService } from '../tenancy/store-context.service';
import { CategoriesRepository } from './categories.repository';

type CategoryWithCount = Category & {
  _count: { products: number };
};

/**
 * Two counts, because two different questions get asked of a category.
 *
 * `totalProductCount` is every product filed under it — what the merchant's
 * delete dialog needs, since a draft is affected just as much as a live one.
 * `productCount` is what a shopper can browse, and it is the one the storefront
 * prints beside the category name.
 *
 * They cannot come from one `_count`: that block counts a *relation*, and the
 * same relation cannot appear twice under it. (An earlier attempt invented an
 * `activeProducts` relation name and failed at runtime with
 * `Unknown field 'activeProducts' ... on model CategoryCountOutputType` — the
 * specs mock Prisma, so nothing caught the invalid shape until the API ran.)
 * So the total comes from `_count` and the active tally from a separate
 * grouped count, the same way the product list resolves variant counts.
 */
const CATEGORY_INCLUDE = {
  _count: { select: { products: true } },
} as const;

@Injectable()
export class PrismaCategoriesRepository implements CategoriesRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async list(parentId?: string): Promise<CategorySummary[]> {
    const parentFilter =
      parentId === 'root' ? null
      : parentId !== undefined ? parentId
      : undefined;

    const categories = await this.prisma.category.findMany({
      where: {
        storeId: this.storeId(),
        isActive: true,
        ...(parentFilter !== undefined ? { parentId: parentFilter } : {}),
      },
      include: CATEGORY_INCLUDE,
      orderBy: { name: 'asc' },
    });

    const activeByCategory = await this.activeProductCounts(categories.map((c) => c.id));

    return categories.map((category) =>
      this.toSummary(category, activeByCategory.get(category.id) ?? 0)
    );
  }

  /**
   * Live products per category, in one grouped query rather than one per row.
   * Categories with no active products are simply absent from the result, so
   * callers default to zero.
   */
  private async activeProductCounts(categoryIds: string[]): Promise<Map<string, number>> {
    if (!categoryIds.length) return new Map();

    const rows = await this.prisma.product.groupBy({
      by: ['categoryId'],
      where: { categoryId: { in: categoryIds }, storeId: this.storeId(), active: true },
      _count: true,
    });

    return new Map(rows.map((row) => [row.categoryId, row._count]));
  }

  async findById(categoryId: string): Promise<CategorySummary | undefined> {
    const category = await this.prisma.category.findFirst({
      where: { id: categoryId, storeId: this.storeId() },
      include: CATEGORY_INCLUDE,
    });

    if (!category) return undefined;

    const active = await this.prisma.product.count({
      where: { categoryId: category.id, storeId: this.storeId(), active: true },
    });

    return this.toSummary(category, active);
  }

  async create(input: CreateCategoryRequest): Promise<CategorySummary> {
    let level = 0;
    if (input.parentId) {
      const parent = await this.prisma.category.findUnique({
        where: { id: input.parentId },
        select: { level: true },
      });
      level = (parent?.level ?? 0) + 1;
    }

    const category = await this.prisma.category.create({
      data: {
        storeId: this.storeId(),
        name: input.name.trim(),
        slug: input.slug.trim().toLowerCase(),
        parentId: input.parentId ?? null,
        level,
      },
      include: CATEGORY_INCLUDE,
    });

    // Nothing has been filed under it yet, so both counts are zero.
    return this.toSummary(category, 0);
  }

  async update(categoryId: string, input: UpdateCategoryRequest): Promise<CategorySummary | undefined> {
    const existing = await this.prisma.category.findFirst({
      where: { id: categoryId, storeId: this.storeId() },
      select: { id: true },
    });

    if (!existing) {
      return undefined;
    }

    const category = await this.prisma.category.update({
      where: { id: categoryId },
      data: {
        ...(input.name !== undefined ? { name: input.name.trim() } : {}),
        ...('parentId' in input ? { parentId: input.parentId ?? null } : {}),
        ...(input.level !== undefined ? { level: input.level } : {}),
      },
      include: CATEGORY_INCLUDE,
    });

    const active = await this.prisma.product.count({
      where: { categoryId: category.id, storeId: this.storeId(), active: true },
    });

    return this.toSummary(category, active);
  }

  async delete(categoryId: string): Promise<boolean> {
    const existing = await this.prisma.category.findFirst({
      where: { id: categoryId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!existing) {
      return false;
    }

    // Collect all active descendant IDs so the entire subtree is deactivated
    const descendantIds = await this.collectDescendantIds(categoryId);
    const allIds = [...descendantIds, categoryId];

    // Soft delete: set isActive = false — no FK constraint issues
    await this.prisma.category.updateMany({
      where: { id: { in: allIds } },
      data: { isActive: false },
    });

    return true;
  }

  private async collectDescendantIds(categoryId: string): Promise<string[]> {
    const children = await this.prisma.category.findMany({
      where: { parentId: categoryId },
      select: { id: true },
    });

    const ids: string[] = [];
    for (const child of children) {
      const grandchildren = await this.collectDescendantIds(child.id);
      ids.push(...grandchildren);
      ids.push(child.id);
    }
    return ids;
  }

  async existsBySlug(slug: string): Promise<boolean> {
    const category = await this.prisma.category.findFirst({
      where: { storeId: this.storeId(), slug: slug.trim().toLowerCase() },
      select: { id: true },
    });

    return Boolean(category);
  }

  private toSummary(category: CategoryWithCount, activeProductCount: number): CategorySummary {
    return {
      id: category.id,
      name: category.name,
      slug: category.slug,
      parentId: category.parentId ?? null,
      level: category.level,
      productCount: activeProductCount,
      totalProductCount: category._count.products,
      createdAt: category.createdAt.toISOString(),
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
